import { Button } from "@/components/ui/button";
import Icons from "@/components/Icons";
import { Loader2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import { getStoredAppIdFallback, getAppId } from "@/lib/token-utils";
import { getGameDataById } from "@/lib/game-config-utils";
import { performDeeplinkRedirect } from "@/lib/deeplink-utils";
import type { GameData } from "@/interfaces";

const InvalidLinkPage = () => {
  const [gameData, setGameData] = useState<GameData | null>(null);
  const [hasAppId, setHasAppId] = useState<boolean>(false);
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);

  useEffect(() => {
    
    // Check for app ID from various sources
    const appIdFromToken = getAppId('any'); // Check both URL and storage
    const appIdFromStorage = getStoredAppIdFallback(); // Check localStorage fallback
    
    const effectiveAppId = appIdFromToken || appIdFromStorage;
    console.log('Effective app ID to use:', effectiveAppId);
    
    if (effectiveAppId) {
      setHasAppId(true);
      
      // Get game data for the app ID
      const gameDataForApp = getGameDataById(effectiveAppId);
      
      if (gameDataForApp) {
        setGameData(gameDataForApp);
        
        // Automatically redirect to the app's default deeplink
        setIsRedirecting(true);
        performDeeplinkRedirect({
          gameId: effectiveAppId,
          gameData: gameDataForApp,
          source: 'any'
        }).catch((error) => {
          console.error('Failed to perform deeplink redirect:', error);
          setIsRedirecting(false);
        });
      } else {
      }
    } else {
      console.log('No app ID found - showing "close window" message');
      setHasAppId(false);
    }
  }, []);

  const handleBackToApp = () => {
    
    if (gameData) {
      setIsRedirecting(true);
      performDeeplinkRedirect({
        gameData,
        source: 'any'
      }).catch((error) => {
        console.error('Failed to perform manual deeplink redirect:', error);
        setIsRedirecting(false);
      });
    } else {
      console.log('No game data available for manual redirect');
    }
  };

  if (isRedirecting) {
    return (
      <section className="flex flex-col flex-1 pt-[17px] pb-[21px] px-3 landscape:h-full landscape:justify-center">
        <div className="flex flex-col justify-center items-center text-center w-full flex-1 landscape:flex-1">
          <Loader2Icon className="animate-spin" size={96} />
          <h1 className="text-[18px] font-medium max-w-40 text-center text-[#423F3B] mb-[14px] landscape:mb-2">
            Redirecting to {gameData?.game_name || "the app"}...
          </h1>
          <p className="text-[#A2998B] flex flex-col items-center landscape:mb-2">
            Please wait while we redirect you back to the app
          </p>
        </div>
      </section>
    );
  }

  
  return (
    <section className="flex flex-col flex-1 pt-[17px] pb-[21px] px-3 landscape:h-full landscape:justify-between">
      <div className="flex flex-col justify-center items-center text-center w-full flex-1 landscape:flex-1">
        <Icons type="checkoutFailed" size={96} />
        <h1 className="text-[18px] font-medium max-w-40 text-center text-[#423F3B] mb-[14px] landscape:mb-2">
          Invalid Link
        </h1>
        {hasAppId && gameData ? (
          <p className="text-[#A2998B] flex flex-col items-center landscape:mb-2">
            This link is not valid. You can return to {gameData.game_name}
          </p>
        ) : (
          <p className="text-[#A2998B] flex flex-col items-center landscape:mb-2">
            You can close this window and go back to the app
          </p>
        )}
      </div>
      {hasAppId && gameData && !isRedirecting && (
        <div className="landscape:flex-shrink-0 w-full landscape:max-w-sm landscape:mx-auto">
          <Button variant="backToGame" onClick={handleBackToApp} className="w-full">
            Back to {gameData.game_name}
          </Button>
        </div>
      )}
    </section>
  );
};

export default InvalidLinkPage;
